import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@api/api.service';
import { MessageService } from '@shared/message/message.service';
import { ModalService } from '@shared/modal/modal.service';
import { EncryptionHelperService } from '@shared/services/encryption-helper.service';
import { HashService } from '@shared/services/hash.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { of } from 'rxjs';
import { retry } from 'rxjs/operators';
import { PasswordValidator } from 'src/app/validator/password.validator';

interface ChangePassword {
  old_pass: string;
  pass: string;
  pass_conf: string;
  password_hash?: string;
  old_password_hash?: string;
}

@Component({
  selector: 'rav-change-password',
  templateUrl: './change-password.component.html'
})
export class ChangePasswordComponent implements OnInit {
  changePasswordFormGroup: FormGroup;
  encryption$ = this.encryptionHelper.accountEncryptionEnabled;

  constructor(
    private api: ApiService,
    private formBuilder: FormBuilder,
    private message: MessageService,
    private localStorage: LocalStorageService,
    private hashService: HashService,
    public modal: ModalService,
    private encryptionHelper: EncryptionHelperService
  ) {}

  ngOnInit() {
    this.buildChangePasswordFormGroup();
  }

  /**
   * Builds the form group
   */
  buildChangePasswordFormGroup(): void {
    this.changePasswordFormGroup = this.formBuilder.group(
      {
        old_pass: new FormControl('', Validators.required),
        pass: new FormControl('', Validators.required),
        pass_conf: new FormControl('', Validators.required)
      },
      { validator: PasswordValidator.MatchPass }
    );
  }

  /**
   * Changes the password for the profile.
   *
   * @param values The new values for the reset.
   */
  async changePassword(values: ChangePassword) {
    values.password_hash = await this.hashService.hash(values.pass);
    values.old_password_hash = await this.hashService.hash(values.old_pass);

    this.api.users.byId.patch(this.localStorage.userWithToken.id, values).subscribe(
      async () => {
        this.buildChangePasswordFormGroup();
        this.modal.close.next();

        await this.updateEncryptedKeypair(values.password_hash);

        this.message.show('change-password-message', {
          message: 'Your password has been changed successfully',
          type: 'success'
        });
      },
      (error) => {
        this.api.onFormError(error, this.changePasswordFormGroup);
      }
    );
  }

  /**
   * Update User-Keypair encryption
   *
   * @param passwordHash New password hash
   */
  async updateEncryptedKeypair(passwordHash: string) {
    const promise = this.encryptionHelper.updateUserKeypairEncryption(passwordHash);

    of(promise).pipe(retry(1)).toPromise();
  }
}
